import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../utils/apiClient';
import './integrations.css';

/**
 * Modal pour configurer les tokens API Atlassian/GitHub/GitLab
 * Chiffre les tokens côté backend avec AES-256-GCM
 * 🆕 Supporte la déconnexion pour changer de credentials
 */
const IntegrationSetupModal = ({ provider, isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    domain: '', // Pour Atlassian et GitLab
    email: '', // Pour Atlassian
    apiToken: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 🆕 État de connexion
  const [isConnected, setIsConnected] = useState(false);
  const [connectedDomain, setConnectedDomain] = useState('');
  const [connectedEmail, setConnectedEmail] = useState('');
  const [disconnecting, setDisconnecting] = useState(false);

  if (!isOpen) return null;

  // 🆕 Vérifier le statut de connexion au montage
  useEffect(() => {
    if (isOpen && provider === 'atlassian') {
      checkConnectionStatus();
    }
  }, [isOpen, provider]);

  const checkConnectionStatus = async () => {
    try {
      const { data } = await apiClient.get('/api/integration/status');
      if (data.atlassian?.connected) {
        setIsConnected(true);
        setConnectedDomain(data.atlassian.domain || '');
        setConnectedEmail(data.atlassian.email || '');
      }
    } catch (err) {
      console.error('Erreur vérification statut:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const endpoint = `/api/integration/${provider}/save-token`;
      const body = provider === 'atlassian'
        ? { domain: formData.domain, email: formData.email, apiToken: formData.apiToken }
        : { apiToken: formData.apiToken };

      const { data } = await apiClient.post(endpoint, body);

      // Succès
      onSuccess && onSuccess(data);
      onClose();
    } catch (err) {
      console.error('Erreur IntegrationSetupModal:', err);
      setError(err.response?.data?.message || err.message || t('jiraIntegration.setup.error'));
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Fonction de déconnexion
  const handleLogout = async () => {
    if (!window.confirm(t('jiraIntegration.logout.confirmMessage'))) {
      return;
    }

    setDisconnecting(true);
    setError(null);

    try {
      const { data } = await apiClient.delete('/api/integration/jira/logout');

      // Succès - Réinitialiser l'état local
      setIsConnected(false);
      setConnectedDomain('');
      setConnectedEmail('');
      setFormData({ domain: '', email: '', apiToken: '' });

      alert(t('jiraIntegration.logout.success'));
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Erreur logout JIRA:', err);
      setError(err.response?.data?.message || err.message || t('jiraIntegration.logout.error'));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getHelpLink = () => {
    if (provider === 'atlassian') {
      return 'https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/';
    } else if (provider === 'github') {
      return 'https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/creating-a-personal-access-token';
    } else if (provider === 'gitlab') {
      return 'https://docs.gitlab.com/ee/user/profile/personal_access_tokens.html';
    }
    return '#';
  };

  return (
    <div className="integration-modal-overlay" onClick={onClose}>
      <div className="integration-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="integration-modal-header">
          <h2>{t('jiraIntegration.setup.title')}</h2>
          <button className="integration-modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* 🆕 Vue conditionnelle: Connecté vs Non connecté */}
        {isConnected && provider === 'atlassian' ? (
          // Vue "Connecté" avec bouton de déconnexion
          <div className="integration-modal-body">
            <div className="integration-connected-view">
              <div className="integration-success-banner">
                <span className="integration-success-icon">✅</span>
                <div>
                  <strong>{t('jiraIntegration.setup.connected', { domain: connectedDomain })}</strong>
                  {connectedEmail && <p className="integration-email">{connectedEmail}</p>}
                </div>
              </div>

              <div className="integration-info-box">
                <h4>{t('jiraIntegration.logout.changeCredentials')}</h4>
                <p>{t('jiraIntegration.logout.changeHint')}</p>
              </div>

              {error && (
                <div className="integration-error-message">
                  <span>⚠️</span> {error}
                </div>
              )}

              <div className="integration-modal-actions">
                <button
                  type="button"
                  onClick={onClose}
                  className="integration-button integration-button-secondary"
                >
                  {t('jiraIntegration.setup.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="integration-button integration-button-danger"
                  disabled={disconnecting}
                >
                  {disconnecting 
                    ? t('jiraIntegration.logout.disconnecting') 
                    : t('jiraIntegration.logout.disconnect')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Vue "Non connecté" (formulaire existant)
          <div className="integration-modal-body">
            <div className="integration-security-notice">
              <span className="integration-security-icon">🔒</span>
              <div>
                <strong>{t('jiraIntegration.security.title')}</strong>
                <p>{t('jiraIntegration.security.encryption')}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="integration-form">
          {provider === 'atlassian' && (
            <>
              <div className="integration-form-group">
                <label htmlFor="domain">{t('jiraIntegration.setup.domain')} *</label>
                <input
                  type="text"
                  id="domain"
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                  placeholder={t('jiraIntegration.setup.domainPlaceholder')}
                  required
                  className="integration-input"
                />
                <small>{t('jiraIntegration.setup.domainHelp')}</small>
              </div>

              <div className="integration-form-group">
                <label htmlFor="email">{t('jiraIntegration.setup.email')} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('jiraIntegration.setup.emailPlaceholder')}
                  required
                  className="integration-input"
                />
              </div>
            </>
          )}

          <div className="integration-form-group">
            <label htmlFor="apiToken">
              {t('jiraIntegration.setup.token')} *
            </label>
            <input
              type="password"
              id="apiToken"
              name="apiToken"
              value={formData.apiToken}
              onChange={handleChange}
              placeholder={t('jiraIntegration.setup.tokenPlaceholder')}
              required
              className="integration-input"
            />
            <small>
              <a href={getHelpLink()} target="_blank" rel="noopener noreferrer">
                {t('jiraIntegration.setup.getToken')}
              </a>
            </small>
          </div>

          {error && (
            <div className="integration-error-message">
              <span>⚠️</span> {error}
            </div>
          )}

          <div className="integration-modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="integration-button integration-button-secondary"
              disabled={loading}
            >
              {t('jiraIntegration.setup.cancel')}
            </button>
            <button
              type="submit"
              className="integration-button integration-button-primary"
              disabled={loading}
            >
              {loading ? t('jiraIntegration.setup.saving') : t('jiraIntegration.setup.save')}
            </button>
          </div>
        </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationSetupModal;
